/**
 * ProjectInfoCard — compact project-metadata sidebar card for the Overview dashboard.
 *
 * Replaces the wide "Project details" metadata grid with a scannable key/value list showing
 * only the fields leadership/PMs need at a glance: type, status, SOW hours + burn-rate bar,
 * planned kickoff, expected completion (with an overdue indicator), planned IPR, and actual
 * completion (with a late indicator + reason). The three workflow date rows carry the same
 * DateChangeRequestButton affordance as the old grid so the request/edit workflow is preserved.
 *
 * V039 additions: Planned IPR joins the date-change-request workflow (PM/SA request → leadership
 * approve), so it gets the button. Actual Completion and Late Reason are plain display — they
 * record what happened rather than a plan to renegotiate, and are edited through the normal
 * PATCH /api/projects/{id} path.
 */
import { AlertTriangle, ExternalLink } from "lucide-react";

import type { DateChangeRequest, Project } from "@/types";
import { cn } from "@/lib/utils";
import { projectTypeLabel } from "@/lib/project-types";
import { DATE_FIELD_LABELS } from "@/lib/date-change";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DateChangeRequestButton } from "@/components/projects/DateChangeRequestButton";

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

/** True when an expected-completion date is in the past (project is overdue). */
function isOverdue(value: string | null): boolean {
  if (!value) return false;
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/** True when the project completed after its expected completion date. */
function completedAfterPlan(
  actual: string | null | undefined,
  expected: string | null | undefined,
): boolean {
  if (!actual || !expected) return false;
  const a = new Date(actual.length === 10 ? `${actual}T00:00:00` : actual);
  const e = new Date(
    expected.length === 10 ? `${expected}T00:00:00` : expected,
  );
  if (Number.isNaN(a.getTime()) || Number.isNaN(e.getTime())) return false;
  return a.getTime() > e.getTime();
}

/** Status → badge variant (mirrors the palette used across the app). */
function statusVariant(status: string | null): BadgeVariant {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return "success";
    case "closed":
      return "neutral";
    case "pending_approval":
      return "warning";
    default:
      return "info";
  }
}

/** Burn-rate bar fill colour: <75% healthy, 75–90% caution, >90% over budget. */
function burnFill(pct: number): string {
  if (pct < 75) return "bg-success";
  if (pct <= 90) return "bg-[#c98a00]";
  return "bg-error";
}

/** One label/value row — label left (muted), value right (emphasised). */
function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-text-secondary">{label}</span>
      <span className="text-sm font-medium text-text-primary">{children}</span>
    </div>
  );
}

const HTTPS_URL_RE = /^https:\/\/\S+$/i;

/** An external document link row (new tab). Renders nothing for an absent / non-https URL. */
function DocLinkRow({
  label,
  url,
}: {
  label: string;
  url: string | null | undefined;
}): JSX.Element | null {
  if (!url || !HTTPS_URL_RE.test(url)) return null;
  return (
    <InfoRow label={label}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={`Open ${label} (opens in a new tab)`}
      >
        Open
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </InfoRow>
  );
}

export interface ProjectInfoCardProps {
  project: Project;
  /** Refetch pending date-change requests after an edit / request is submitted. */
  onDateChangeRequested: () => void;
  /** Unresolved date-change requests for this project (drives the per-field "Pending" badge). */
  pendingDateRequests: DateChangeRequest[];
}

export function ProjectInfoCard({
  project,
  onDateChangeRequested,
  pendingDateRequests,
}: ProjectInfoCardProps): JSX.Element {
  const burnPct =
    project.burn_rate_pct != null ? Math.round(project.burn_rate_pct) : null;
  const burnWidth = burnPct != null ? Math.min(Math.max(burnPct, 0), 100) : 0;
  const overdue = isOverdue(project.expected_completion_date);
  const completedLate = completedAfterPlan(
    project.actual_completion_date,
    project.expected_completion_date,
  );

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-text-primary">
          Project Info
        </h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <InfoRow label="Type">
            <Badge variant="neutral" dot={false}>
              {projectTypeLabel(project.project_type)}
            </Badge>
          </InfoRow>

          <InfoRow label="Status">
            <Badge variant={statusVariant(project.status)} dot={false}>
              {project.status || "—"}
            </Badge>
          </InfoRow>

          {/* Consumed vs SOW hours + burn-rate bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-text-secondary">
                Consumed / SOW Hours
              </span>
              <span className="text-sm font-medium text-text-primary">
                {project.hours_consumed ?? 0} / {project.sow_hours ?? "—"}
              </span>
            </div>
            <div
              className="h-1.5 w-full overflow-hidden rounded bg-outlines"
              role="progressbar"
              aria-valuenow={burnPct ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Burn rate: ${burnPct != null ? `${burnPct}%` : "not available"}`}
            >
              {burnPct != null && (
                <div
                  className={cn("h-full rounded", burnFill(burnPct))}
                  style={{ width: `${burnWidth}%` }}
                />
              )}
            </div>
            <div className="flex justify-end">
              <span className="text-[11px] text-text-secondary">
                Burn rate: {burnPct != null ? `${burnPct}%` : "N/A"}
              </span>
            </div>
          </div>

          {/* Planned kickoff — inline date-change affordance */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-text-secondary">Planned Kickoff</span>
            <DateChangeRequestButton
              projectId={project.jira_key}
              dateField="planned_kickoff_date"
              currentValue={project.planned_kickoff_date}
              label="Planned Kickoff"
              hasPending={pendingDateRequests.some(
                (r) => r.date_field === "planned_kickoff_date",
              )}
              onRequested={onDateChangeRequested}
            />
          </div>

          {/* Expected completion — inline date-change affordance + overdue flag */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-text-secondary">
              Expected Completion
            </span>
            <div className="flex items-center gap-2">
              {overdue && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium text-error"
                  title={`Overdue — expected ${formatDate(project.expected_completion_date)}`}
                >
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                  Overdue
                </span>
              )}
              <DateChangeRequestButton
                projectId={project.jira_key}
                dateField="expected_completion_date"
                currentValue={project.expected_completion_date}
                label="Expected Completion"
                hasPending={pendingDateRequests.some(
                  (r) => r.date_field === "expected_completion_date",
                )}
                onRequested={onDateChangeRequested}
              />
            </div>
          </div>
          {/* Planned IPR — joined the date-change-request workflow in V039, so it gets the same
              affordance as the other two workflow dates rather than a plain display row. */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-text-secondary">
              {DATE_FIELD_LABELS.planned_ipr_date}
            </span>
            <DateChangeRequestButton
              projectId={project.jira_key}
              dateField="planned_ipr_date"
              currentValue={project.planned_ipr_date ?? null}
              label={DATE_FIELD_LABELS.planned_ipr_date}
              hasPending={pendingDateRequests.some(
                (r) => r.date_field === "planned_ipr_date",
              )}
              onRequested={onDateChangeRequested}
            />
          </div>

          {/* Actual completion — plain display. Not part of the change-request workflow: it is a
              record of what happened, not a plan to be renegotiated. Edited via PATCH
              /api/projects/{id} (pm on own projects, leadership, admin). */}
          <InfoRow label="Actual Completion">
            <span className={cn(completedLate && "text-error")}>
              {formatDate(project.actual_completion_date ?? null)}
            </span>
          </InfoRow>

          {/* T-48/49 (V055): documents a migrated project already had before DeliverPro. Rendered
              only when set, and only for https links (the API and DB CHECK enforce https; the
              re-check here keeps a javascript:/data: URL from ever becoming an href). */}
          <DocLinkRow label="Existing SRS" url={project.existing_srs_url} />
          <DocLinkRow
            label="Existing Kickoff Deck"
            url={project.existing_kickoff_deck_url}
          />

          {/* Late reason — rendered only when a reason exists. The API enforces that a reason can
              only exist on a genuinely-late completion (422 LATE_REASON_NOT_APPLICABLE), so an
              always-visible "—" row would be dead weight on the ~majority of projects. Full text
              wraps rather than truncating: a half-shown explanation is worse than none. */}
          {project.late_reason && (
            <div className="space-y-1">
              <span className="text-xs text-text-secondary">Late Reason</span>
              <p className="whitespace-pre-wrap text-sm font-medium text-text-primary">
                {project.late_reason}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ProjectInfoCard;
